import type { CollectionData } from "@/types/collectionTypes";
import type { CourseProgressCardProps } from "@/components/collection/CourseProgressCard";
import type { CollectionDetailLayoutContentAreaProps } from "./CollectionDetailLayout";

export interface BuildContentAreaArgs {
  displayCollectionData: CollectionData | null;
  contentId: string | undefined;
  isTrackable: boolean;
  isAuthenticated: boolean;
  hasBatchInRoute: boolean;
  isEnrolledInCurrentBatch: boolean;
  contentBlocked: boolean;
  upcomingBatchBlocked: boolean;
  isBatchEnded?: boolean;
  batchStartDateForOverview: string | undefined;
  playerMetadata: unknown;
  playerIsLoading: boolean;
  playerError: Error | null;
  handlePlayerEvent: (event: any) => void;
  handleTelemetryEvent: (event: any) => void;
  maxAttemptsExceeded: boolean;
  courseProgressProps: CourseProgressCardProps | null | undefined;
  contentStatusMap: Record<string, number> | undefined;
  contentAttemptInfoMap: Record<string, { attemptCount: number }> | undefined;
  batches: unknown;
  selectedBatchId: string;
  setSelectedBatchId: (id: string) => void;
  cdata?: Array<{ id: string; type: string }>;
  objectRollup?: Record<string, string>;
  handleJoinCourse: (batchId: string) => void;
  batchListLoading: boolean;
  joinLoading: boolean;
  batchListError: string | undefined;
  joinError: string;
  hasCertificate: boolean;
  firstCertPreviewUrl: string | undefined;
  setCertificatePreviewUrl: (url: string) => void;
  setCertificatePreviewOpen: (open: boolean) => void;
  enrolledDate?: number | string;
  contentStateFetched?: boolean;
  expandedModules: string[];
  toggleModule: (moduleId: string) => void;
  collectionId: string | undefined;
  batchIdParam: string | undefined;
  isCreatorViewingOwnCollection: boolean;
  isMentorViewingCourse: boolean;
  contentCreatorPrivilege: boolean;
  userProfile: Record<string, unknown> | undefined;
  currentUserId: string | undefined;
  backTo?: string;
}

export function buildCollectionDetailContentArea(
  args: BuildContentAreaArgs
): CollectionDetailLayoutContentAreaProps | null {
  const { displayCollectionData } = args;
  if (!displayCollectionData) return null;
  return {
    collectionData: displayCollectionData,
    contentId: args.contentId,
    access: {
      isTrackable: args.isTrackable,
      isAuthenticated: args.isAuthenticated,
      hasBatchInRoute: args.hasBatchInRoute,
      isEnrolledInCurrentBatch: args.isEnrolledInCurrentBatch,
      contentBlocked: args.contentBlocked,
      upcomingBatchBlocked: args.upcomingBatchBlocked,
      isBatchEnded: args.isBatchEnded,
      batchStartDateForOverview: args.batchStartDateForOverview,
    },
    player: {
      playerMetadata: args.playerMetadata,
      playerIsLoading: args.playerIsLoading,
      playerError: args.playerError,
      handlePlayerEvent: args.handlePlayerEvent,
      handleTelemetryEvent: args.handleTelemetryEvent,
      showMaxAttemptsExceeded: args.maxAttemptsExceeded,
      cdata: args.cdata,
      objectRollup: args.objectRollup,
    },
    enrollment: {
      courseProgressProps: args.courseProgressProps,
      contentStatusMap: args.contentStatusMap,
      contentAttemptInfoMap: args.contentAttemptInfoMap,
      batches: args.batches as CollectionDetailLayoutContentAreaProps["enrollment"]["batches"],
      selectedBatchId: args.selectedBatchId,
      setSelectedBatchId: args.setSelectedBatchId,
      handleJoinCourse: args.handleJoinCourse,
      batchListLoading: args.batchListLoading,
      joinLoading: args.joinLoading,
      batchListError: args.batchListError,
      joinError: args.joinError,
      hasCertificate: args.hasCertificate,
      firstCertPreviewUrl: args.firstCertPreviewUrl,
      setCertificatePreviewUrl: args.setCertificatePreviewUrl,
      setCertificatePreviewOpen: args.setCertificatePreviewOpen,
      enrolledDate: args.enrolledDate,
      contentStateFetched: args.contentStateFetched,
    },
    sidebar: {
      expandedModules: args.expandedModules,
      toggleModule: args.toggleModule,
      collectionId: args.collectionId,
      batchIdParam: args.batchIdParam,
    },
    creator: {
      isCreatorViewingOwnCollection: args.isCreatorViewingOwnCollection,
      isMentorViewingCourse: args.isMentorViewingCourse,
      contentCreatorPrivilege: args.contentCreatorPrivilege,
      userProfile: args.userProfile,
      userId: args.currentUserId,
    },
    backTo: args.backTo,
  };
}
